import { CHANNEL_ID, LOG_ID } from '@@types/common';
import Member from '@@types/Member';

import { EMPTY_LOG_ID, UNREAD_LOG_ID } from '~/constants';
import { OFFICE_HOURS_KEY } from '~/constants/member';
import { isDirectChannel } from '~/services/channelService';
import Actions from '~/stores/Actions';
import { channelLogsStore } from '~/stores/ChannelLogsStore';
import { channelsStore } from '~/stores/ChannelsStore';
import { mainDetailUiState } from '~/stores/DetailUiState';
import { interTenantMembersStore } from '~/stores/InterTenantMembersStore';
import { officeHoursStore } from '~/stores/OfficeHoursStore';
import { stores } from '~/stores/Stores';
import { userStore } from '~/stores/UserStore';
import { all, complement, forEach, last, pipe, pluck, propEq, reject, tap } from 'ramda';

class OfficeHoursActions {
  constructor(private actions: Actions) {}

  public revalidateMeInWork = () => {
    const member = interTenantMembersStore.getById(userStore.id);
    officeHoursStore.revalidateIsInOfficeHours(member);
    officeHoursStore.prepareForChangeTime(this.revalidateMeInWork, userStore.id)([userStore.id]);
  };

  public revalidateChannelNotInWork = () => {
    if (!this.actions.permissionActions.showNotInWork) {
      this.setNotInWorkChannel(null);
      return;
    }
    this.hasOutOfOfficeHoursMember(mainDetailUiState.contentsChannelId).then((value) => {
      officeHoursStore.setIsInOfficeHour(OFFICE_HOURS_KEY.CHANNEL, !value);
      if (!value) {
        this.setNotInWorkChannel(null);
        return;
      }
      if (
        channelLogsStore.notInWorkLogId &&
        mainDetailUiState.contentsChannelId === channelLogsStore.notInWorkChannelId
      ) {
        return;
      }
      this.setNotInWorkChannel(last(channelLogsStore.logList)?.id ?? EMPTY_LOG_ID, mainDetailUiState.contentsChannelId);
      let prevent = false;
      if (mainDetailUiState.preventToOfficeHoursScrollLogId) {
        prevent =
          !!channelLogsStore.getMainLogById(mainDetailUiState.preventToOfficeHoursScrollLogId) ||
          mainDetailUiState.preventToOfficeHoursScrollLogId.endsWith(UNREAD_LOG_ID);
      }

      if (
        !channelLogsStore.newLogId &&
        !mainDetailUiState.highlightTarget &&
        !mainDetailUiState.scrollTarget &&
        !prevent
      ) {
        mainDetailUiState.scrollToBottom();
      }
    });
  };

  private setNotInWorkChannel = (logId: LOG_ID | null, channelId?: CHANNEL_ID) => {
    if (logId === null) {
      channelLogsStore.setNotInWorkLogId(null);
      officeHoursStore.setIsInOfficeHour(OFFICE_HOURS_KEY.CHANNEL, true);
    } else {
      channelLogsStore.setNotInWorkLogId(logId, channelId);
      officeHoursStore.setIsInOfficeHour(OFFICE_HOURS_KEY.CHANNEL, false);
    }
  };

  private hasOutOfOfficeHoursMember = async (channelId: CHANNEL_ID): Promise<boolean> => {
    const channel = channelsStore.getById(channelId);
    try {
      const members = (
        isDirectChannel(channel)
          ? [await this.actions.memberActions.getOrFetchMemberById(channel.opponentId, channelId)]
          : await this.actions.channelMembersActions.getOrFetchChannelMembers(channel)
      ) as Member[];
      const membersWithoutMe = reject(propEq('id', stores.user.id), members);
      forEach(officeHoursStore.revalidateIsInOfficeHours)(membersWithoutMe);
      const result = complement(all(officeHoursStore.isInOfficeHours))(membersWithoutMe);
      pipe(
        pluck('id'),
        tap(officeHoursStore.pickCurrentChannelMembers),
        officeHoursStore.prepareForChangeTime(this.revalidateChannelNotInWork, OFFICE_HOURS_KEY.CHANNEL)
      )(membersWithoutMe);
      return result;
    } catch (e) {
      return false;
    }
  };
}

export default OfficeHoursActions;
